import "chonky/style/main.css";
// style overrides
import "@/modules/storage/s3/chonky.css";

import FF from "@/components/forms/FormField.module.css";
import RA from "@/components/forms/Radio.module.css";
import R from "@/components/forms/Radiogroup.module.css";
import TF from "@/components/forms/TextField.module.css";
import P from "@/components/Progress.module.css";
import C from "@/modules/storage/s3/S3FileBrowser.module.css";

import { WButton } from "@/components/button/WButton.tsx";
import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { controllerProps, formPropsToAriaProps } from "@/components/forms/ariaController.tsx";
import { WCheckbox } from "@/components/forms/WCheckbox.tsx";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { WTextField } from "@/components/forms/WTextField.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { LongDate } from "@/components/l10n/DateFormat.tsx";
import { Percent } from "@/components/l10n/Percent.tsx";
import { Loading } from "@/components/loading/Loading.tsx";
import { ModalRow } from "@/components/modal/ModalRow.tsx";
import { showModal } from "@/components/modal/registerModal.tsx";
import { WModalContent } from "@/components/modal/WModal.tsx";
import { Spinner } from "@/components/Spinner.tsx";
import { WTooltip } from "@/components/WTooltip.tsx";
import { useConfig } from "@/config.ts";
import { isMockApiEnabled } from "@/mock-api/msw/mswInit.ts";
import { useApiClient } from "@/modules/api/apiClient.store.ts";
import { getResourceIconClass } from "@/modules/api/resourceTypes.tsx";
import { notifyError } from "@/modules/error/errorReporting.ts";
import { raiseBackgroundErrorToast, raiseToast, type Toast } from "@/modules/notifications/toast.tsx";
import { bucketCorsRequest } from "@/modules/storage/s3/api.ts";
import { formatBytes } from "@/modules/storage/s3/utils.ts";
import { cn } from "@/utils/classNames.ts";
import { unsetPropertyCheckProxy } from "@/utils/debug/unsetPropertyCheckProxy.ts";
import { Sha256 } from "@aws-crypto/sha256-browser";
import {
    AbortMultipartUploadCommand,
    CompleteMultipartUploadCommand,
    CopyObjectCommand,
    CreateMultipartUploadCommand,
    DeleteObjectCommand,
    GetObjectAclCommand,
    type GetObjectAclCommandOutput,
    GetObjectCommand,
    ListObjectsV2Command,
    type ListObjectsV2CommandOutput,
    type ObjectCannedACL,
    PutObjectAclCommand,
    PutObjectCommand,
    S3Client,
    type S3ClientConfig,
    UploadPartCommand,
} from "@aws-sdk/client-s3";
import type { CommonPrefix, Grant, _Object as S3Object } from "@aws-sdk/client-s3/dist-types/models";
import { getSignedUrl, S3RequestPresigner } from "@aws-sdk/s3-request-presigner";
import { formatUrl } from "@aws-sdk/util-format-url";
import type { I18n, MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { HttpRequest } from "@smithy/protocol-http";
import { parseUrl } from "@smithy/url-parser";
import { useBlocker } from "@tanstack/react-router";
import { singleElement } from "@warrenio/utils/collections/singleElement";
import { isObjectWithKey } from "@warrenio/utils/isObject";
import { randomUUID } from "@warrenio/utils/ponyfill/randomUUID";
import { discardAsync, discardPromise } from "@warrenio/utils/promise/discardPromise";
import {
    ChonkyActions,
    ChonkyIconName,
    type FileAction,
    type FileActionData,
    type FileActionHandler,
    type FileArray,
    FileBrowser,
    type FileData,
    FileHelper,
    FileList,
    FileSearch,
    FileToolbar,
} from "chonky";
import type { ToolbarButtonData } from "chonky/lib/types/file-actions.types";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Input, Radio, RadioGroup } from "react-aria-components";
import { useDropzone } from "react-dropzone";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";

// cSpell:words presign

const UPLOAD_CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks

/** Maximum records to fetch in one chunk */
const MAX_KEYS = 1000;
/** Maximum related objects that can be deleted when deleting a "folder" */
const DELETE_MAX_KEYS = 2000;
/** Maximum related objects that can be copied over during rename action on a "folder" */
const RENAME_MAX_KEYS = 2000;

/** Maximum number of chunks to be loaded before pausing and asking user to continue */
const CHUNK_COUNTER_PAUSE = 10; // 10 x 'MaxKeys' (1000) = 10 000 records

/** Maximum number of chunks to be loaded to prevent browser hanging or crashing */
const CHUNK_COUNTER_LIMIT = 100; // 100 x 'MaxKeys' (1000) = 100 000 records

interface UploadProgressFile {
    /** Stable client ID used before S3 assigns a multipart upload ID. */
    id: string;
    orderNr: number;
    /** Assigned by CreateMultipartUpload; absent while that request is pending. */
    uploadId?: string;
    key: FileData["id"];
    modDate: Exclude<FileData["modDate"], undefined>;
    size: number;
    uploadedSize: number;
    // 'finalized' means that upload is complete and also added to 'files' state variable
    status: "in_progress" | "complete" | "finalized" | "failed" | "cancelled";
    errorMessage?: string;
    cancelInProgress: boolean;
    /** Whether multipart cleanup failed and must be retried before dismissing the failed upload. */
    multipartCleanupFailed: boolean;
}

interface UploadStats {
    totalSize: number;
    uploadedSize: number;
    totalFiles: number;
    uploadedFiles: number;
    percentage: number;
}

type BrowserLoadStateType = "idle" | "loading" | "paused" | "finished" | "halted";

interface ShareLinkResult {
    path_style_url: string;
    vhost_style_url?: string;
    expires: number; // timestamp
}

interface MyFileAction extends Omit<FileAction, "toolbarButton"> {
    mobileIconOnly?: boolean;
    toolbarButton?: Omit<ToolbarButtonData, "name" | "tooltip" | "group"> & {
        name: MessageDescriptor | string;
        tooltip?: MessageDescriptor | string;
        group?: MessageDescriptor | string;
    };
}

const msg_Access = msg({
    comment: "Label for dropdown menu that has options related to file sharing and permissions. | POS: Noun",
    message: "Access",
});

//#region Toolbar actions

const MyOpenFilesAction = {
    id: "open_files",
    requiresSelection: true,
    hotkeys: ["enter"],
} satisfies MyFileAction;

const MyToggleShowFoldersFirst = {
    id: "toggle_show_folders_first",
    option: {
        id: "show_folders_first",
        defaultValue: true,
    },
} satisfies MyFileAction;

const msg_download = msg({
    comment: "Button action to download a file. | POS: Verb",
    message: "Download",
});
const msg_upload = msg({
    comment: "Button action to upload a file. | POS: Verb",
    message: "Upload",
});

const MyDownloadFiles = {
    id: "download_files",
    requiresSelection: true,
    mobileIconOnly: true,
    toolbarButton: {
        name: msg_download,
        tooltip: msg_download,
        icon: ChonkyIconName.download,
    },
} satisfies MyFileAction;

const MyUploadFiles = {
    id: "upload_files",
    mobileIconOnly: true,
    toolbarButton: {
        name: msg_upload,
        tooltip: msg_upload,
        icon: ChonkyIconName.upload,
    },
} satisfies MyFileAction;

const MyDeleteFiles = {
    id: "delete_files",
    requiresSelection: true,
    hotkeys: ["delete"],
    mobileIconOnly: true,
    toolbarButton: {
        name: msg`Delete`,
        tooltip: msg`Delete`,
        icon: ChonkyIconName.trash,
    },
} satisfies MyFileAction;

const MyCreateFolder = {
    id: "create_folder",
    mobileIconOnly: true,
    toolbarButton: {
        name: msg`New folder`,
        tooltip: msg`New folder`,
        icon: ChonkyIconName.folderCreate,
    },
} satisfies MyFileAction;

const MyRenameFiles = {
    id: "rename_files",
    requiresSelection: true,
    toolbarButton: {
        name: msg`Rename`,
        tooltip: msg`Rename`,
        icon: ChonkyIconName.fallbackIcon,
    },
} satisfies MyFileAction;

const MyShare = {
    id: "share",
    requiresSelection: true,
    toolbarButton: {
        name: msg`Share temporarily`,
        tooltip: msg`Share temporarily`,
        icon: ChonkyIconName.fallbackIcon,
        group: msg_Access,
        dropdown: true,
    },
} satisfies MyFileAction;

const MyPermissions = {
    id: "permissions",
    requiresSelection: true,
    toolbarButton: {
        name: msg`Permissions`,
        tooltip: msg`Permissions`,
        icon: ChonkyIconName.fallbackIcon,
        group: msg_Access,
        dropdown: true,
    },
} satisfies MyFileAction;

const MyCopyPublicUrl = {
    id: "copy_public_url",
    requiresSelection: true,
    toolbarButton: {
        name: msg`Copy public URL`,
        tooltip: msg`Copy public URL`,
        icon: ChonkyIconName.fallbackIcon,
        group: msg_Access,
        dropdown: true,
    },
} satisfies MyFileAction;

// Override default buttons so they can be localized
const ChangeSelection: MyFileAction = {
    ...ChonkyActions.ChangeSelection,
};

const OpenParentFolder: MyFileAction = {
    ...ChonkyActions.OpenParentFolder,
};

const EnableListView: MyFileAction = {
    ...ChonkyActions.EnableListView,
    toolbarButton: {
        ...ChonkyActions.EnableListView.toolbarButton,
        name: msg`Switch to List view`,
    },
};

const EnableGridView: MyFileAction = {
    ...ChonkyActions.EnableGridView,
    toolbarButton: {
        ...ChonkyActions.EnableGridView.toolbarButton,
        name: msg`Switch to Grid view`,
    },
};

const group_sort = msg({
    comment: "Label for dropdown menu that selects by what files are sorted. | POS: Verb",
    message: "Sort",
});

const SortFilesByName: MyFileAction = {
    ...ChonkyActions.SortFilesByName,
    toolbarButton: {
        ...ChonkyActions.SortFilesByName.toolbarButton,
        group: group_sort,
        name: msg`Sort by name`,
    },
};

const SortFilesBySize: MyFileAction = {
    ...ChonkyActions.SortFilesBySize,
    toolbarButton: {
        ...ChonkyActions.SortFilesBySize.toolbarButton,
        group: group_sort,
        name: msg`Sort by size`,
    },
};

const SortFilesByDate: MyFileAction = {
    ...ChonkyActions.SortFilesByDate,
    toolbarButton: {
        ...ChonkyActions.SortFilesByDate.toolbarButton,
        group: group_sort,
        name: msg`Sort by date`,
    },
};

function translateAction(isMobile: boolean, action: MyFileAction, i18n: I18n): FileAction {
    function t(s: MessageDescriptor | string): string {
        return typeof s === "string" ? s : i18n._(s);
    }
    function t_undef(s: MessageDescriptor | string | undefined) {
        return s == null ? s : t(s);
    }
    const toolbarButton: ToolbarButtonData | undefined = action.toolbarButton && {
        ...action.toolbarButton,
        // Display only icon on mobile devices
        name: isMobile && action.mobileIconOnly ? "" : t(action.toolbarButton.name),
        tooltip: t_undef(action.toolbarButton.tooltip),
        group: t_undef(action.toolbarButton.group),
    };
    return { ...action, toolbarButton };
}

//#endregion

const LOADING_FILES: FileArray = [null];

export interface S3ClientProps {
    accessKeyId: string;
    secretAccessKey: string;
    endpoint: string;
    forcePathStyle: boolean;
    // TODO: Region is a new required V3 property that we do not currently have available.
    region: string;
}

export interface S3FileBrowserProps {
    clientOptions: S3ClientProps;
    bucketName: string;
    mobileView: boolean;
    mockLoadState?: BrowserLoadStateType;
}

export default function S3FileBrowser({
    clientOptions: { accessKeyId, secretAccessKey, endpoint, forcePathStyle, region },
    bucketName,
    mobileView,
    // Mocks
    mockLoadState,
}: S3FileBrowserProps): ReactNode {
    const { i18n, t } = useLingui();
    const [currentFolderId, setCurrentFolderId] = useState<string>("");
    const [isInitializing, setIsInitializing] = useState<boolean>(true);
    const [folderChain, setFolderChain] = useState<FileData[]>([]);
    const [files, setFiles] = useState<FileData[]>([]);
    const [uploadProgressFiles, setUploadProgressFiles] = useState<UploadProgressFile[]>([]);
    // Client IDs bridge cancellation requests made before CreateMultipartUpload has returned an S3 upload ID.
    const cancelledUploadIds = useRef(new Set<string>());

    const hasActiveUploads = uploadProgressFiles.some((upload) => upload.status === "in_progress");
    useBlocker({
        shouldBlockFn: () =>
            !window.confirm(
                t`Uploads are still in progress. If you leave this page, progress will no longer be shown and uploads may be interrupted. Leave anyway?`,
            ),
        enableBeforeUnload: hasActiveUploads,
        disabled: !hasActiveUploads,
    });

    const [browserLoadState, setBrowserLoadState] = useState<BrowserLoadStateType>("idle");
    const [chunkCounter, setChunkCounter] = useState<number>(0);
    const [continueMarker, setContinueMarker] = useState<string | undefined>(undefined);
    const [loadTrigger, setLoadTrigger] = useState<number>(0);

    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    // NB: Undo fullscreen when unmounting (to prevent from page being stuck in fullscreen when eg. pressing Back)
    useEffect(() => {
        return () => {
            updateFullScreenState(false);
        };
    }, []);

    const [isUploadOpen, setIsUploadOpen] = useState<boolean>(false);

    const apiClient = useApiClient();

    useEffect(() => {
        const searchInput = document.getElementById("chonky-file-search");

        if (searchInput) {
            searchInput.setAttribute("placeholder", t`Search`);
        }
    }, []);

    useEffect(() => {
        // setting up CORS for browsing buckets
        discardAsync(async () => {
            try {
                await bucketCorsRequest(apiClient, {
                    name: bucketName,
                    allowed_origin: document.location.origin,
                });
            } catch (err) {
                raiseBackgroundErrorToast(err, "Failed to configure bucket CORS", toastOptions);
                return;
            }

            setCurrentFolderId("");
            setFolderChain([{ id: " ", name: bucketName, isDir: true }]);
            setIsInitializing(false);
        });
    }, []);

    const s3 = useMemo(() => {
        const params: S3ClientConfig = {
            apiVersion: "latest",
            credentials: {
                accessKeyId,
                secretAccessKey,
            },
            region,
            endpoint,
            forcePathStyle,
        };
        return !import.meta.env.PROD && isMockApiEnabled() ? MockS3.make(params) : new S3Client(params);
    }, [accessKeyId, secretAccessKey, endpoint, forcePathStyle, region]);

    const isMock = !import.meta.env.PROD && s3 instanceof MockS3;

    //#region Mock effects
    useEffect(() => {
        if (!import.meta.env.PROD && isMockApiEnabled()) {
            setUploadProgressFiles(mockUploadProgress);
        }
    }, []);

    useEffect(() => {
        if (mockLoadState) {
            setBrowserLoadState(mockLoadState);
        }
    }, [mockLoadState]);
    //#endregion

    useEffect(() => {
        // NB: Must not call S3 before CORS is set up
        if (isInitializing) {
            return;
        }

        const prefix = currentFolderId.trim(); // XXX: Is the trim necessary?
        setBrowserLoadState("loading");
        discardAsync(async () => {
            try {
                const _files = await listS3BucketFiles(s3, bucketName, prefix);
                if (chunkCounter >= CHUNK_COUNTER_PAUSE) {
                    // append to files
                    setFiles((prevFiles) =>
                        _files.filter((_f) => !prevFiles.some((_prevF) => _prevF.id === _f.id) /* ensure unique ids*/),
                    );
                } else {
                    // replace file list
                    setFiles(_files);
                }
            } catch (err) {
                if (isObjectWithKey(err, "code")) {
                    if (err.code === "NoSuchBucket") {
                        Toast_exception(err, `Bucket '${bucketName}' does not exist. Please check the bucket name.`);
                    } else {
                        Toast_exception(err, "Error listing bucket");
                    }
                } else {
                    console.error("Unknown S3 error occurred", err);
                    notifyError(err, { tags: ["s3_startup_list"] });
                }
                setFiles([]); // remove loader
            }
        });
    }, [isInitializing, currentFolderId, loadTrigger, mockFiles]);

    const handleFileAction = useCallback<FileActionHandler>(
        (action: FileAction, actionData: FileActionData) => {
            console.log("action[%s]: %o", action.id, actionData);
            if (browserLoadState === "loading") {
                return;
            }

            function startFileDownload(file: FileData) {
                if (!file.isDir) {
                    const command = new GetObjectCommand({
                        Bucket: bucketName,
                        Key: file.id,
                        ResponseContentDisposition: `attachment; filename="${file.name}"`,
                    });
                    isMock
                        ? s3.getSignedUrl(file, 3600)
                        : getSignedUrl(s3, command, { expiresIn: 60 }).then((url) => {
                              window.open(url, "_blank");
                              Analytics_event({ action: "File Download Initiated" });
                          });
                } else {
                    Toast_error("This action is available only for files");
                }
            }

            const { id } = action;
            if (id === MyCopyPublicUrl.id) {
                const file = singleElement(actionData.files);
                if (file) {
                    if (!file.isDir) {
                        navigator.clipboard.writeText(getObjectsPublicUrl(file.id)).then(() => {
                            Toast_info("Object's public URL copied to clipboard");
                        });
                    } else {
                        Toast_error("This action is available only for files");
                    }
                } else {
                    Toast_error("This action is available only for one file");
                }
            } else if (id === MyShare.id) {
                const file = actionData.files![0];
                if (!file.isDir) {
                    showModal(
                        <ShareModal
                            file={file}
                            callback={async (linkAge) => {
                                Analytics_event({
                                    action: "Share Temp File Link Initiated",
                                });
                                return await generateShareLink(file, linkAge);
                            }}
                        />,
                    );
                } else {
                    Toast_error("This action is available only for files");
                }
            } else if (id === MyPermissions.id) {
                const files = actionData.files!;
                const containsDirs = files.find((f: FileData) => f.isDir);
                if (!containsDirs) {
                    const singleFile = singleElement(files);

                    showModal(
                        <PermissionsModal
                            s3={s3}
                            bucketName={bucketName}
                            publicUrl={singleFile ? getObjectsPublicUrl(singleFile.id) : undefined}
                            files={files}
                            callback={async (permission) => {
                                Analytics_event({ action: "Set Permissions Initiated" });
                                return await setFilePermission(files, permission);
                            }}
                        />,
                    );
                } else {
                    Toast_error("This action is available only for files");
                }
            } else if (id === MyRenameFiles.id) {
                const selectedFiles = actionData.files!;
                if (selectedFiles.length === 1) {
                    const file = selectedFiles[0];

                    showModal(
                        <RenameFileOrFolderModal
                            file={file}
                            callback={async (newName) => {
                                Analytics_event({ action: "File Rename Initiated" });
                                return await renameFileOrFolder(file, newName);
                            }}
                        />,
                    );
                } else {
                    Toast_error("This action is available only for one file or folder");
                }
            } else if (id === MyDeleteFiles.id) {
                const files = actionData.files!;

                showModal(
                    <DeleteFileOrFolderModal
                        files={files}
                        callback={async () => {
                            Analytics_event({ action: "File Delete Initiated" });
                            return await deleteFileOrFolder(files);
                        }}
                    />,
                );
            } else if (id === ChonkyActions.UploadFiles.id) {
                Analytics_event({ action: "File Upload Initiated" });
                open();
            } else if (id === MyDownloadFiles.id) {
                const selectedFiles = actionData.files!;
                if (selectedFiles.length === 1) {
                    startFileDownload(selectedFiles[0]);
                } else {
                    Toast_error("This action is available only for one file");
                }
            } else if (id === MyOpenFilesAction.id) {
                //let targetFile: Nullable<FileData> = null;
                let targetFile: FileData | null = null;
                if (actionData.target) {
                    targetFile = actionData.target;
                } else if (actionData.files && actionData.files.length === 1) {
                    targetFile = actionData.files[0];
                }

                if (targetFile && FileHelper.isDirectory(targetFile)) {
                    setFiles([]);
                    setCurrentFolderId(targetFile.id);

                    setChunkCounter(0);
                    setBrowserLoadState("idle");
                    setContinueMarker(undefined);

                    const existingLinkInChainIndex = folderChain.findIndex((dir) => dir && dir.id === targetFile.id);
                    if (existingLinkInChainIndex !== -1) {
                        // navigating upward in directory chain
                        setFolderChain(folderChain.slice(0, existingLinkInChainIndex + 1));
                    } else {
                        // navigating into sub-directory, add folder to the end of chain
                        setFolderChain([...folderChain, targetFile]);
                    }
                    return;
                } else {
                    startFileDownload(targetFile!);
                    return;
                }
            } else if (id === MyCreateFolder.id) {
                showModal(
                    <CreateFolderModal
                        callback={async (name) => {
                            Analytics_event({ action: "Folder Create Initiated" });
                            return await createFolder(name);
                        }}
                    />,
                );
            }
        },
        [s3, currentFolderId, folderChain, browserLoadState, isFullscreen],
    );

    async function listS3BucketFiles(s3: S3Client, bucket: string, prefix: string) {
        const files: FileData[] = [];
        const s3Items: S3FilesAndDirs = await listS3Keys(s3, bucket, prefix);
        const prefixReplaceRegex = new RegExp(`^${prefix}`);

        for (const obj of s3Items.files) {
            const key = obj.Key!;
            if (key !== prefix) {
                files.push({
                    id: key,
                    name: key.split("/").pop()!, // remove directory path from file name
                    modDate: obj.LastModified,
                    size: obj.Size,
                });
            }
        }
        for (const obj of s3Items.dirs) {
            files.push({
                id: obj.Prefix!,
                name: obj.Prefix!.replace(prefixReplaceRegex, "").replace(/\/+$/, ""), // remove also trailing slash as file browser component adds this automatically
                isDir: true,
            });
        }
        return files;
    }

    interface S3FilesAndDirs {
        files: S3Object[];
        dirs: CommonPrefix[];
    }

    async function listS3Keys(s3client: S3Client, bucket: string, prefix: string): Promise<S3FilesAndDirs> {
        let continuationToken: string | undefined = continueMarker;
        const itemList: S3FilesAndDirs = {
            files: [],
            dirs: [],
        };
        let counter = chunkCounter;
        do {
            const command = new ListObjectsV2Command({
                Prefix: prefix,
                Bucket: bucket,
                ContinuationToken: continuationToken,
                MaxKeys: MAX_KEYS,
                Delimiter: "/",
            });
            const res = await s3client.send(command);

            counter += 1;

            continuationToken = res.NextContinuationToken ?? undefined;
            itemList.files = [...itemList.files, ...(res.Contents ?? [])];
            itemList.dirs = [...itemList.dirs, ...(res.CommonPrefixes ?? [])];
        } while (continuationToken !== undefined && counter % CHUNK_COUNTER_PAUSE && counter < CHUNK_COUNTER_LIMIT);

        if (counter >= CHUNK_COUNTER_LIMIT) {
            setChunkCounter(0);
            setBrowserLoadState("halted");
            setContinueMarker(undefined);
        } else if (continuationToken === undefined) {
            setChunkCounter(0);
            setBrowserLoadState("finished");
            setContinueMarker(undefined);
        } else {
            setChunkCounter(counter);
            setBrowserLoadState("paused");
            setContinueMarker(continuationToken);
        }

        return itemList;
    }

    const fileActions = useMemo(
        () =>
            [
                MyCreateFolder,
                MyUploadFiles,

                MyDownloadFiles,
                MyDeleteFiles,
                MyRenameFiles,

                ChangeSelection,

                OpenParentFolder,

                MyShare,
                MyPermissions,
                MyCopyPublicUrl,

                EnableListView,
                EnableGridView,

                MyOpenFilesAction,

                SortFilesByName,
                SortFilesBySize,
                SortFilesByDate,

                MyToggleShowFoldersFirst,
            ].map((action): FileAction => translateAction(mobileView, action, i18n)),
        [mobileView, i18n.locale],
    );

    function loadMore() {
        setLoadTrigger(loadTrigger + 1);
    }

    useEffect(() => {
        function escFunction(event: KeyboardEvent) {
            if (event.key === "Escape") {
                updateFullScreenState(false);
            }
        }

        document.addEventListener("keydown", escFunction, false);

        return () => {
            document.removeEventListener("keydown", escFunction, false);
        };
    }, []);

    useEffect(() => {
        const completedUploads = [...uploadProgressFiles.filter((uf: UploadProgressFile) => uf.status === "complete")];
        if (completedUploads.length) {
            const finalizedUploads = completedUploads.map(
                (cu: UploadProgressFile): UploadProgressFile => ({
                    ...cu,
                    status: "finalized",
                }),
            );
            setUploadProgressFiles([
                ...uploadProgressFiles.filter((uf: UploadProgressFile) => uf.status !== "complete"),
                ...finalizedUploads,
            ]);

            for (const completeUpload of completedUploads) {
                setFiles((prevFiles) => {
                    if (prevFiles.find((_f) => _f.id === completeUpload.key)) {
                        // ensure that id's are always unique in files' list
                        return [...prevFiles];
                    } else {
                        // update files' list only if user is currently located in the same directory that the file was uploaded to
                        const fileName = completeUpload.key.split("/").pop()!;
                        if (completeUpload.key.replace(fileName, "") === currentFolderId) {
                            return [
                                ...prevFiles,
                                {
                                    id: completeUpload.key,
                                    name: fileName,
                                    modDate: completeUpload.modDate,
                                    size: completeUpload.size,
                                },
                            ];
                        }
                        return prevFiles;
                    }
                });
            }
        }

        const finalizedUploads = [...uploadProgressFiles.filter((uf: UploadProgressFile) => uf.status === "finalized")];
        if (uploadProgressFiles.length > 0 && finalizedUploads.length === uploadProgressFiles.length) {
            // empty progress array if all uploads are finalized
            setUploadProgressFiles([]);
            setIsUploadOpen(false);
        }
    }, [uploadProgressFiles]);

    const uploadStats = useMemo<UploadStats>(() => {
        const totalSize = uploadProgressFiles.reduce((a, b) => +a + +b.size, 0);
        const uploadedSize = uploadProgressFiles.reduce((a, b) => +a + +b.uploadedSize, 0);
        return {
            totalSize,
            uploadedSize,
            totalFiles: uploadProgressFiles.length,
            uploadedFiles: uploadProgressFiles.filter(
                (p: UploadProgressFile) => p.status === "complete" || p.status === "finalized",
            ).length,
            percentage: (uploadedSize / totalSize) * 100,
        };
    }, [uploadProgressFiles]);

    async function uploadFileWithMultipart(s3client: S3Client, file: File) {
        const id = randomUUID();
        const command = new CreateMultipartUploadCommand({
            Bucket: bucketName,
            Key: `${currentFolderId}${file.name}`,
            ContentType: file.type,
        });

        // Register before the first request so navigation protection also covers multipart initialization.
        setUploadProgressFiles((prevState) => [
            ...prevState,
            {
                id,
                orderNr: prevState.length + 1,
                key: command.input.Key!, // TODO: Fix this type
                modDate: new Date(),
                size: file.size,
                uploadedSize: 0,
                status: "in_progress",
                cancelInProgress: false,
                multipartCleanupFailed: false,
            },
        ]);

        let data;
        try {
            data = await s3client.send(command);
        } catch (err) {
            cancelledUploadIds.current.delete(id);
            setUploadProgressFiles((prevState) => prevState.filter((upload) => upload.id !== id));
            Toast_exception(err, "Error creating multipart upload");
            return;
        }

        const uploadId = data.UploadId!;
        const totalChunks = Math.ceil(file.size / UPLOAD_CHUNK_SIZE);

        setUploadProgressFiles((prevState) =>
            prevState.map((upload) => (upload.id === id ? { ...upload, uploadId } : upload)),
        );

        // Cancellation may have been requested while CreateMultipartUpload was still pending.
        if (cancelledUploadIds.current.delete(id)) {
            try {
                await abortMultipartUpload(s3client, bucketName, command.input.Key!, uploadId);
                setUploadProgressFiles((prevState) => prevState.filter((upload) => upload.id !== id));
            } catch (err) {
                setUploadProgressFiles((prevState) =>
                    prevState.map((upload) =>
                        upload.id === id
                            ? {
                                  ...upload,
                                  status: "failed",
                                  errorMessage: `Cancelling failed: ${getErrorMessage(err)}`,
                                  cancelInProgress: false,
                                  multipartCleanupFailed: true,
                              }
                            : upload,
                    ),
                );
                Toast_exception(err, "Error cancelling upload");
            }
            return;
        }

        async function uploadParts() {
            const parts = [];

            for (let partNumber = 1; partNumber <= totalChunks; partNumber++) {
                const start = (partNumber - 1) * UPLOAD_CHUNK_SIZE;
                const end = Math.min(partNumber * UPLOAD_CHUNK_SIZE, file.size);

                const chunk = file.slice(start, end);

                const uploadPartCommand = new UploadPartCommand({
                    Bucket: command.input.Bucket,
                    Key: command.input.Key,
                    UploadId: uploadId,
                    PartNumber: partNumber,
                    Body: chunk,
                });

                const partData = await s3client.send(uploadPartCommand);
                parts.push({ ETag: partData.ETag, PartNumber: partNumber });

                setUploadProgressFiles((prevState: UploadProgressFile[]) =>
                    prevState.map((uploadFile) =>
                        uploadFile.id === id
                            ? {
                                  ...uploadFile,
                                  uploadedSize: Math.min(partNumber * UPLOAD_CHUNK_SIZE, uploadFile.size),
                              }
                            : uploadFile,
                    ),
                );
            }

            return parts;
        }

        try {
            const parts = await uploadParts();
            const completeCommand = new CompleteMultipartUploadCommand({
                Bucket: command.input.Bucket,
                Key: command.input.Key,
                UploadId: uploadId,
                MultipartUpload: {
                    Parts: parts,
                },
            });

            await s3client.send(completeCommand);

            // Completion can win a race with cancellation; record it and let the abort path reconcile via a reload.
            cancelledUploadIds.current.delete(id);

            setUploadProgressFiles((prevState: UploadProgressFile[]) =>
                prevState.map((uploadFile) =>
                    uploadFile.id === id
                        ? { ...uploadFile, uploadedSize: uploadFile.size, status: "complete" }
                        : uploadFile,
                ),
            );
        } catch (err) {
            if (cancelledUploadIds.current.delete(id)) {
                // Aborting the multipart session is expected to reject the active part or completion request.
                return;
            }

            setUploadProgressFiles((prevState: UploadProgressFile[]) => {
                const activeFile = prevState.find((upload) => upload.id === id && upload.status === "in_progress");
                if (activeFile) {
                    return prevState.map((uploadFile) =>
                        uploadFile.id === id
                            ? {
                                  ...uploadFile,
                                  uploadedSize: 0,
                                  status: "failed",
                                  errorMessage: `Uploading failed: ${getErrorMessage(err)}`,
                                  cancelInProgress: true,
                              }
                            : uploadFile,
                    );
                }
                return prevState;
            });

            // S3 retains successfully uploaded parts after a multipart failure until the session is explicitly aborted.
            try {
                await abortMultipartUpload(s3client, bucketName, command.input.Key!, uploadId);
            } catch (cleanupError) {
                setUploadProgressFiles((prevState) =>
                    prevState.map((uploadFile) =>
                        uploadFile.id === id ? { ...uploadFile, multipartCleanupFailed: true } : uploadFile,
                    ),
                );
                Toast_exception(cleanupError, "Error cleaning up failed multipart upload");
            } finally {
                setUploadProgressFiles((prevState) =>
                    prevState.map((uploadFile) =>
                        uploadFile.id === id ? { ...uploadFile, cancelInProgress: false } : uploadFile,
                    ),
                );
            }
        }
    }

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            Analytics_event({ action: "File Upload Initiated" });

            for (const file of acceptedFiles) {
                discardPromise(uploadFileWithMultipart(s3, file));
            }
        },
        [currentFolderId],
    );

    const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
        onDrop,
        noClick: true,
        noKeyboard: true,
        noDragEventsBubbling: true,
    });

    // clear or cancel upload, depending on context
    async function clearUpload(uploadFile: UploadProgressFile): Promise<boolean> {
        if (uploadFile.errorMessage && uploadFile.status === "failed") {
            if (uploadFile.multipartCleanupFailed && uploadFile.uploadId) {
                setUploadProgressFiles((prevState) =>
                    prevState.map((file) => (file.id === uploadFile.id ? { ...file, cancelInProgress: true } : file)),
                );
                try {
                    const abortResult = await abortMultipartUpload(s3, bucketName, uploadFile.key, uploadFile.uploadId);
                    if (abortResult === "already_missing") {
                        // A prior completion may explain the missing session; refresh before dismissing the failure.
                        reloadData();
                    }
                } catch (err) {
                    setUploadProgressFiles((prevState) =>
                        prevState.map((file) =>
                            file.id === uploadFile.id ? { ...file, cancelInProgress: false } : file,
                        ),
                    );
                    Toast_exception(err, "Error cleaning up failed multipart upload");
                    return false;
                }
            }
            setUploadProgressFiles((prevState: UploadProgressFile[]) => [
                ...prevState.filter((upload) => upload.id !== uploadFile.id),
            ]);
            return true;
        } else {
            cancelledUploadIds.current.add(uploadFile.id);
            setUploadProgressFiles((prevState: UploadProgressFile[]) => {
                const activeFile = prevState.find((upload) => upload.id === uploadFile.id);
                if (activeFile) {
                    activeFile.cancelInProgress = true;
                    activeFile.status = "cancelled";
                    return [...prevState.filter((upload) => upload.id !== uploadFile.id), activeFile];
                }
                return [...prevState];
            });

            if (!uploadFile.uploadId) {
                // Creation is still pending; uploadFileWithMultipart will handle cancellation if S3 provides an ID.
                return true;
            }

            try {
                const abortResult = await abortMultipartUpload(s3, bucketName, uploadFile.key, uploadFile.uploadId);
                setUploadProgressFiles((prevState: UploadProgressFile[]) => [
                    ...prevState.filter((upload) => upload.id !== uploadFile.id),
                ]);
                if (abortResult === "already_missing") {
                    // Completion may have won the cancellation race; refresh so the resulting object is visible.
                    reloadData();
                }
                return true;
            } catch (err) {
                cancelledUploadIds.current.delete(uploadFile.id);
                setUploadProgressFiles((prevState) =>
                    prevState.map((file) =>
                        file.id === uploadFile.id
                            ? {
                                  ...file,
                                  status: "failed",
                                  errorMessage: `Cancelling failed: ${getErrorMessage(err)}`,
                                  cancelInProgress: false,
                                  multipartCleanupFailed: true,
                              }
                            : file,
                    ),
                );
                Toast_exception(err, "Error cancelling upload");
                return false;
            }
        }
    }

    async function clearAllUploads() {
        // set 'status' and 'cancelInProgress' props up front,
        // so that UI doesn't allow to cancel file a second time with 'Cancel' button click
        setUploadProgressFiles((prevState: UploadProgressFile[]) => {
            for (const uf of prevState) {
                if (uf.status !== "complete" && uf.status !== "finalized") {
                    uf.cancelInProgress = true;
                    uf.status = uf.status !== "failed" ? "cancelled" : uf.status;
                }
            }
            return [...prevState];
        });

        const notCompletedUploadFiles = uploadProgressFiles.filter(
            (up: UploadProgressFile) => up.status !== "complete" && up.status !== "finalized",
        );

        for (const file of notCompletedUploadFiles) {
            await clearUpload(file);
        }
        setIsUploadOpen(false);
    }

    async function deleteFileOrFolder(files: FileData[]): Promise<boolean> {
        async function deleteObjectsInDirectory(prefix: string): Promise<boolean> {
            try {
                const listCommand = new ListObjectsV2Command({
                    Bucket: bucketName,
                    Prefix: prefix,
                    MaxKeys: DELETE_MAX_KEYS,
                });

                const listData = await s3.send(listCommand);
                if (listData.Contents) {
                    for (const object of listData.Contents) {
                        const deleteCommand = new DeleteObjectCommand({
                            Bucket: bucketName,
                            Key: object.Key!,
                        });
                        await s3.send(deleteCommand);
                    }
                }

                if (listData.CommonPrefixes) {
                    for (const commonPrefix of listData.CommonPrefixes) {
                        await deleteObjectsInDirectory(commonPrefix.Prefix!);
                    }
                }

                return true;
            } catch (err) {
                Toast_exception(err, "Error deleting objects in directory");
                return false;
            }
        }

        try {
            await Promise.all(
                files.map(async (file: FileData) => {
                    if (file.isDir) {
                        const result = await deleteObjectsInDirectory(file.id);
                        if (result) {
                            setFiles((prevState) => prevState.filter((f) => !f.id.startsWith(file.id)));
                        }
                        return result;
                    } else {
                        try {
                            await s3.send(new DeleteObjectCommand({ Bucket: bucketName, Key: file.id }));
                            setFiles((prevState) => prevState.filter((f) => f.id !== file.id));
                            return true;
                        } catch (err) {
                            Toast_exception(err, "Error deleting file");
                            return false;
                        }
                    }
                }),
            );
            // XXX: This is wrong if any of the promises fail
            return true;
        } catch (err) {
            // XXX: Useless, since all errors are caught in the inner functions
            Toast_exception(err, "Some files or folders were not deleted because error occurred");
            return false;
        }
    }

    async function generateShareLink(file: FileData, age: number): Promise<ShareLinkResult | null> {
        try {
            const command = new GetObjectCommand({
                Bucket: bucketName,
                Key: file.id,
            });
            const signedUrl = isMock ? s3.getSignedUrl(file, age) : await getSignedUrl(s3, command, { expiresIn: age });
            const url = new URL(signedUrl);

            const expires = Number(url.searchParams.get("X-Amz-Expires"));
            const protocol = url.protocol;
            const subdomain = url.hostname.split(".")[0];
            const domain = url.hostname.split(".").slice(1).join(".");
            const pathAndQuery = url.pathname + url.search;

            const presigner = new S3RequestPresigner({
                credentials: s3.config.credentials,
                region: s3.config.region,
                sha256: Sha256,
            });

            const signedUrlObject = await presigner.presign(
                new HttpRequest({ ...parseUrl(`${protocol}//${domain}/${subdomain}${pathAndQuery}`) }),
                { expiresIn: age },
            );
            const pathStyleUrl =
                (forcePathStyle || bucketName.includes(".")) && !isMock ? url : formatUrl(signedUrlObject);

            const result: ShareLinkResult = {
                path_style_url: pathStyleUrl.toString(),
                expires,
            };

            if ((!forcePathStyle && !bucketName.includes(".")) || isMock) {
                result.vhost_style_url = url.toString();
            }
            return result;
        } catch (err) {
            Toast_exception(err, "Generating share URL failed");
            return null;
        }
    }

    async function setFilePermission(files: FileData[], permission: ObjectCannedACL): Promise<boolean> {
        try {
            // Fix this type
            await Promise.all(
                files.map(async (f: FileData) => {
                    const command = new PutObjectAclCommand({
                        Bucket: bucketName,
                        Key: f.id,
                        ACL: permission,
                    });

                    return await s3.send(command);
                }),
            );
            return true;
        } catch (err) {
            Toast_exception(err, "Error setting permission");
            return false;
        }
    }

    async function renameFileOrFolder(file: FileData, newName: string): Promise<boolean> {
        const keyChunks = file.id.split("/");

        async function renameObjectsInDirectory(prefix: string) {
            const listCommand = new ListObjectsV2Command({
                Bucket: bucketName,
                Prefix: prefix,
                MaxKeys: RENAME_MAX_KEYS,
            });
            const listData = await s3.send(listCommand);
            if (listData.Contents) {
                for (const object of listData.Contents) {
                    const sourceKey = object.Key!;
                    const keyChunks = prefix.replace(/\/+$/, "").split("/");
                    keyChunks.pop();
                    keyChunks.push(newName);
                    const targetKeyStart = `${keyChunks.join("/")}/`;

                    const targetKey = sourceKey.replace(new RegExp(`^${prefix}`), targetKeyStart);

                    const _isDir = targetKey[targetKey.length - 1] === "/";
                    const targetChunks = targetKey.split("/");
                    const _newName = _isDir
                        ? targetChunks[targetChunks.length - 2]
                        : targetChunks[targetChunks.length - 1];

                    await renameObject(sourceKey, targetKey, _newName, _isDir);
                }
            }

            if (listData.CommonPrefixes) {
                for (const commonPrefix of listData.CommonPrefixes) {
                    await renameObjectsInDirectory(commonPrefix.Prefix || "");
                }
            }
        }

        async function renameObject(
            sourceKey: string,
            targetKey: string,
            objName: string,
            isDir: boolean,
        ): Promise<boolean> {
            const copyResult = await s3.send(
                new CopyObjectCommand({
                    Bucket: bucketName,
                    CopySource: `${bucketName}/${sourceKey}`,
                    Key: targetKey,
                }),
            );

            let isSameLevelObject = false;
            if (currentFolderId.split("/").length === `${targetKey.replace(/\/+$/, "")}/`.split("/").length - 1) {
                isSameLevelObject = true;
            }

            if (isSameLevelObject) {
                setFiles((prevState) => {
                    const newObj: FileData = {
                        id: targetKey,
                        name: objName,
                        isDir,
                    };
                    if (!isDir) {
                        newObj.modDate = copyResult.CopyObjectResult!.LastModified;
                    }

                    return [...prevState.filter((pf) => pf.id !== targetKey), newObj];
                });
            }

            await s3.send(new DeleteObjectCommand({ Bucket: bucketName, Key: sourceKey }));

            setFiles((prevState) => prevState.filter((f) => f.id !== sourceKey));

            return true;
        }

        try {
            if (file.isDir) {
                await renameObjectsInDirectory(file.id);
            } else {
                keyChunks.pop();
                keyChunks.push(newName);
                const targetKey = keyChunks.join("/");
                const sourceKey = file.id;

                await renameObject(sourceKey, targetKey, newName, false);
            }
            return true;
        } catch (err) {
            Toast_exception(err, "Error renaming file or directory");
            return false;
        }
    }

    async function createFolder(value: string): Promise<boolean> {
        const name = currentFolderId ? `${currentFolderId.trim() + value.trim()}/` : `${value.trim()}/`;

        try {
            await s3.send(new PutObjectCommand({ Bucket: bucketName, Key: name }));
            reloadData();
            return true;
        } catch (err) {
            Toast_exception(err, "Error creating folder");
            return false;
        }
    }

    function reloadData() {
        setFiles([]); // empty files' list and show loader
        setChunkCounter(0);
        setContinueMarker(undefined);
        loadMore();
    }

    function updateFullScreenState(newFullScreen: boolean) {
        document.body.classList.toggle("codeEditorFullScreen", newFullScreen);
        setIsFullscreen(newFullScreen);
    }

    function uploadFilesHolder() {
        if (uploadProgressFiles.length === 0) {
            return null;
        }

        const hasClearableUploads = uploadProgressFiles.some(
            (p) => p.status === "in_progress" || p.status === "failed",
        );

        return (
            <div className={C.Files}>
                <div className={cn(C.Heading, isUploadOpen && C.Open)}>
                    <div className="flex-grow">
                        <div className="flex justify-between">
                            <div>
                                <b>
                                    <Trans>Uploading files</Trans>
                                </b>
                            </div>
                            <div className="text-right">
                                <span>
                                    <Trans>
                                        {uploadStats.uploadedFiles} of {uploadStats.totalFiles} files uploaded
                                    </Trans>
                                </span>{" "}
                                <span>
                                    ({formatBytes(uploadStats.uploadedSize)}/{formatBytes(uploadStats.totalSize)}){" "}
                                    <b>
                                        <Percent value={uploadStats.percentage} maximumFractionDigits={1} />
                                    </b>
                                </span>
                            </div>
                        </div>

                        <progress className={P.Progress} value={uploadStats.percentage.toFixed(1)} max={100} />
                    </div>

                    <WButton color="primary" action={() => setIsUploadOpen(!isUploadOpen)}>
                        {isUploadOpen ? <Trans>Hide details</Trans> : <Trans>Show details</Trans>}
                    </WButton>
                </div>

                {isUploadOpen && (
                    <div className={cn(C.Content, isFullscreen && C.Full)}>
                        {uploadProgressFiles
                            .filter((p) => p.status !== "complete" && p.status !== "finalized")
                            .sort((a, b) => (a.orderNr > b.orderNr ? 1 : -1))
                            .map((uf) => (
                                <div key={uf.id} className={C.Item}>
                                    <div className={C.Title}>{uf.key}</div>
                                    <div className="flex-grow">
                                        {uf.errorMessage ? (
                                            <div className="text-error">{uf.errorMessage}</div>
                                        ) : (
                                            <progress
                                                className={P.Progress}
                                                value={((uf.uploadedSize / uf.size) * 100).toFixed(1)}
                                                max={100}
                                            />
                                        )}
                                    </div>
                                    {(uf.status === "in_progress" || uf.status === "failed") && (
                                        <WButton
                                            icon="jp-icon-close"
                                            variant="ghost"
                                            size="xs"
                                            color={uf.status === "in_progress" ? "primary" : "error"}
                                            isDisabled={uf.cancelInProgress}
                                            className="ml-3"
                                            action={() => {
                                                clearUpload(uf);
                                            }}
                                        />
                                    )}
                                </div>
                            ))}
                    </div>
                )}

                {isUploadOpen && hasClearableUploads && (
                    <div className={cn(C.Item, C.Cancel, isFullscreen && C.Full)}>
                        <WButton variant="ghost" color="error" action={clearAllUploads}>
                            {uploadProgressFiles.some((p) => p.status === "in_progress") ? (
                                <Trans>Cancel uploads</Trans>
                            ) : (
                                <Trans>Clear uploads</Trans>
                            )}
                        </WButton>
                    </div>
                )}
            </div>
        );
    }

    function getFullScreenDivHeight() {
        return isFullscreen
            ? ["paused", "halted", "loading"].includes(browserLoadState)
                ? uploadProgressFiles && !!uploadProgressFiles.length
                    ? isUploadOpen
                        ? "calc(100% - 48px - 62px - 258px)"
                        : "calc(100% - 48px - 62px)"
                    : "calc(100% - 48px)"
                : uploadProgressFiles && !!uploadProgressFiles.length
                  ? isUploadOpen
                      ? "calc(100% - 62px - 258px)"
                      : "calc(100% - 62px)"
                  : "100%"
            : undefined;
    }

    function getObjectsPublicUrl(objectKey: string): string {
        return forcePathStyle
            ? `${endpoint}${bucketName}/${objectKey}`
            : `https://${bucketName}.${endpoint.replace("https://", "")}${objectKey}`;
    }

    if (isInitializing) {
        return <Loading icon="none" />;
    }

    return (
        <div className={isFullscreen ? C.Fullscreen : undefined}>
            {!isFullscreen && uploadFilesHolder()}

            <div className="responsive-table" style={{ height: getFullScreenDivHeight() }}>
                <div className={isFullscreen ? "h-100%" : undefined}>
                    <div className={cn(isFullscreen && "h-100%", C.dragDropArea)} {...getRootProps()}>
                        <input {...getInputProps()} />

                        <div className={C.Buttons}>
                            <WButton
                                variant="ghost"
                                color="muted"
                                icon="jp-icon-refresh"
                                size="xs"
                                action={() => reloadData()}
                            >
                                <Trans>Reload data</Trans>
                            </WButton>

                            <WButton
                                className="hidden! lg:inline-flex!"
                                variant="ghost"
                                color="muted"
                                icon={isFullscreen ? "jp-zoom-out-icon" : "jp-zoom-in-icon"}
                                size="xs"
                                action={() => updateFullScreenState(!isFullscreen)}
                            >
                                {isFullscreen ? <Trans>Minimize</Trans> : <Trans>Maximize</Trans>}
                            </WButton>
                        </div>

                        <FileBrowser
                            defaultFileViewActionId={ChonkyActions.EnableListView.id}
                            disableDefaultFileActions={true}
                            fileActions={fileActions}
                            files={browserLoadState === "loading" && files.length === 0 ? LOADING_FILES : files}
                            folderChain={folderChain}
                            onFileAction={handleFileAction}
                            enableDragAndDrop={false}
                        >
                            <FileToolbar />
                            <FileSearch />
                            <div className={cn(C.FileList, isFullscreen && C.Full)}>
                                <FileList />
                            </div>
                        </FileBrowser>

                        {isDragActive ? (
                            <div className={C.Empty}>
                                <div>
                                    <MaskIcon className="jp-icon-file-upload size-4rem mb-3" />
                                    <p className="font-size-small">
                                        <Trans>Drop files here</Trans>
                                        <br />
                                        <Trans>Files will be uploaded to</Trans> "/{bucketName}/{currentFolderId}"
                                    </p>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            <div
                className={cn(
                    C.LoadMore,
                    isFullscreen && C.Full,
                    ["paused", "halted", "loading"].includes(browserLoadState) && C.State,
                )}
            >
                {browserLoadState === "paused" && (
                    <>
                        <WButton color="primary" className="mr-3" action={loadMore}>
                            <Trans>Load more</Trans>
                        </WButton>
                        <p className="text-muted">
                            <Trans>
                                Your storage bucket contains a substantial number of files, and we've temporarily halted
                                the loading process. Simply click the "Load More" button to continue loading the next
                                batch of files.
                            </Trans>
                        </p>
                    </>
                )}

                {browserLoadState === "halted" && (
                    <p className="text-muted">
                        <Trans>
                            We've reached the capacity of our file browser. In order to maintain the stability of our
                            user interface, we've terminated the loading of your bucket contents. Please utilize
                            external tools at your disposal to manage the content within your bucket
                        </Trans>
                    </p>
                )}

                {browserLoadState === "loading" && (
                    <>
                        <div className="mr-3" style={{ width: 32, height: 32, position: "relative" }}>
                            <Spinner />
                        </div>
                        <p className="text-muted">
                            <Trans>Loading...</Trans>
                        </p>
                    </>
                )}
            </div>

            {isFullscreen && uploadFilesHolder()}
        </div>
    );
}

function getErrorMessage(error: unknown) {
    const message = error instanceof Error ? error.message : undefined;
    return isObjectWithKey(error, "code")
        ? message
            ? `${message}, code: '${error.code}'`
            : `'${error.code}'`
        : String(error);
}

type AbortMultipartUploadResult = "aborted" | "already_missing";

async function abortMultipartUpload(
    s3client: S3Client,
    bucketName: string,
    key: string,
    uploadId: string,
): Promise<AbortMultipartUploadResult> {
    try {
        await s3client.send(
            new AbortMultipartUploadCommand({
                Bucket: bucketName,
                Key: key,
                UploadId: uploadId,
            }),
        );
        return "aborted";
    } catch (err) {
        if (
            (isObjectWithKey(err, "name") && err.name === "NoSuchUpload") ||
            (isObjectWithKey(err, "code") && err.code === "NoSuchUpload")
        ) {
            // The cleanup objective is already met. During cancellation this can also mean completion won the race.
            return "already_missing";
        }
        throw err;
    }
}

//#region Mocks

function generateETag(): string {
    // Dummy ETag generator for illustration; replace with actual logic as needed
    return `"${Math.random().toString(36).substring(2, 34)}"`;
}

const mockDate = new Date("2024-06-22T12:34:56Z");

const mockUploadProgress: UploadProgressFile[] = [
    {
        id: "mock-client-1",
        orderNr: 1,
        uploadId: "mock-1",
        key: "half-progress",
        modDate: mockDate,
        size: 1000000,
        uploadedSize: 500000,
        status: "in_progress",
        cancelInProgress: false,
        multipartCleanupFailed: false,
    },
    {
        id: "mock-client-2",
        orderNr: 2,
        uploadId: "mock-2",
        key: "error-state",
        modDate: mockDate,
        size: 5000000,
        uploadedSize: 100000,
        status: "failed",
        errorMessage: "Upload failed: Something went wrong",
        cancelInProgress: false,
        multipartCleanupFailed: false,
    },
];

// Refactor to initialize mockFiles and mockDirectories correctly
let mockFiles: S3Object[] = [
    {
        Key: "file1.txt",
        LastModified: mockDate,
        ETag: generateETag(),
        Size: 100,
        StorageClass: "STANDARD",
    },
    {
        Key: "file2.txt",
        LastModified: mockDate,
        ETag: generateETag(),
        Size: 200,
        StorageClass: "STANDARD",
    },
];

const mockDirectories: CommonPrefix[] = [
    {
        Prefix: "dir1/",
    },
];

class MockS3 {
    constructor(public readonly config: S3ClientConfig) {}

    static make(params: S3ClientConfig) {
        return unsetPropertyCheckProxy(new MockS3(params)) as unknown as S3Client & MockS3;
    }

    getFormattedDate() {
        const now = new Date();
        const year = now.getUTCFullYear();
        const month = String(now.getUTCMonth() + 1).padStart(2, "0");
        const day = String(now.getUTCDate()).padStart(2, "0");
        const hours = String(now.getUTCHours()).padStart(2, "0");
        const minutes = String(now.getUTCMinutes()).padStart(2, "0");
        const seconds = String(now.getUTCSeconds()).padStart(2, "0");
        return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
    }

    getAmzDate(date: Date) {
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, "0");
        const day = String(date.getUTCDate()).padStart(2, "0");
        return `${year}${month}${day}`;
    }

    // Usage
    async send(command: unknown): Promise<unknown> {
        switch (true) {
            // Create
            case command instanceof CreateMultipartUploadCommand:
                mockFiles.push({
                    Key: command.input.Key,
                    LastModified: new Date(),
                    Size: 100,
                });
                return Promise.resolve({});

            // Read
            case command instanceof ListObjectsV2Command: {
                const listResp: ListObjectsV2CommandOutput = {
                    $metadata: {},
                    Contents: mockFiles.filter((file) => file.Key?.startsWith(command.input.Prefix!)),
                    CommonPrefixes: mockDirectories.filter((dir) => dir.Prefix?.startsWith(command.input.Prefix!)),
                    Prefix: command.input.Prefix,
                    Delimiter: "/",
                    Name: command.input.Bucket,
                    IsTruncated: false,
                };
                return Promise.resolve(listResp);
            }

            case command instanceof GetObjectAclCommand: {
                const mockUser = "mock-user@example.com";
                const aclResp: GetObjectAclCommandOutput = {
                    $metadata: {},
                    Grants: [
                        {
                            Grantee: {
                                DisplayName: mockUser,
                                ID: mockUser,
                                Type: "CanonicalUser",
                            },
                            Permission: "FULL_CONTROL",
                        },
                    ],
                    Owner: {
                        DisplayName: mockUser,
                        ID: mockUser,
                    },
                };
                return Promise.resolve(aclResp);
            }

            // Update
            case command instanceof PutObjectCommand:
                mockDirectories.push({
                    Prefix: command.input.Key,
                });
                return Promise.resolve({});

            // Delete
            case command instanceof DeleteObjectCommand:
                mockFiles = mockFiles.filter((file) => file.Key !== command.input.Key);
                return Promise.resolve({});

            // Default
            default:
                return notImplemented();
        }
    }

    getSignedUrl(file: FileData, age: number) {
        const now = new Date();
        const amzDate = this.getFormattedDate();
        const amzDateShort = this.getAmzDate(now);

        // cSpell:disable-next-line
        const search = `?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Content-Sha256=UNSIGNED-PAYLOAD&X-Amz-Credential=A9BP2C6UDTZ5FBNATQOQ%2F${amzDateShort}%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=${amzDate}&X-Amz-Expires=${age}&X-Amz-Signature=603e0995f5957a6d0c728e714b0759c7600d07eafa99d6fc657c7afd94c6b7c1&X-Amz-SignedHeaders=host&x-id=GetObject`;
        // TODO: Use real endpoint from config
        const host = "mock-endpoint";
        return new URL(`https://${host}/${file.name}${search}`);
    }

    createMultipartUpload() {
        notImplemented();
    }

    uploadPart() {
        notImplemented();
    }

    completeMultipartUpload() {
        notImplemented();
    }
}

function notImplemented(): never {
    throw new Error("S3 Mock Not implemented");
}

//#endregion

//#region Legacy compatibility stubs

function Analytics_event(event: { action: string }): void {
    const _category = "S3 File Browser";
    console.debug("S3 analytics event:", event);
}

const toastOptions: Partial<Toast> = { icon: getResourceIconClass("bucket") };

function Toast_exception(err: unknown, prefix: string) {
    raiseBackgroundErrorToast(err, prefix, toastOptions);
}

function Toast_error(message: string): void {
    raiseToast({ ...toastOptions, type: "error", message });
}

function Toast_success(message: string): void {
    raiseToast({ ...toastOptions, type: "success", message });
}

function Toast_info(message: string): void {
    raiseToast({ ...toastOptions, type: "info", message });
}

//#endregion

interface DeleteFileOrFolderInputs {
    confirmed: boolean;
}

function DeleteFileOrFolderModal({ files, callback }: { files: FileData[]; callback: () => Promise<boolean> }) {
    const { t } = useLingui();
    const [_deleteInProgress, setDeleteInProgress] = useState<boolean>(false);
    const autoConfirmed = files.length === 1 && !files[0].isDir;

    const singleFile = singleElement(files);
    const isMultipleFilesOrDir = !singleFile || singleFile.isDir;

    let headerElements;
    if (singleFile) {
        const name = <strong>"{singleFile.name}"</strong>;
        headerElements = (
            <>
                <ModalRow title={singleFile.isDir ? t`Folder` : t`File`}>
                    <Trans>{name} will be deleted permanently.</Trans>
                </ModalRow>
                <ModalRow title={t`Full path`}>
                    <strong>"/{singleFile.id}"</strong>
                </ModalRow>
            </>
        );
    } else {
        const count = <strong>{files.length}</strong>;
        headerElements = (
            <div>
                <Trans comment="Items: means files or folders">
                    Deleting {count} <Plural value={files.length} one="item" other="items" />.
                </Trans>
            </div>
        );
    }

    const onSubmit: SubmitHandler<DeleteFileOrFolderInputs> = async (_inputs) => {
        setDeleteInProgress(true);
        await callback();
        setValue("confirmed", false);
    };

    const form = useForm<DeleteFileOrFolderInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            confirmed: autoConfirmed,
        },
    });

    const { control, setValue, watch } = form;

    return (
        <WModalForm
            className="max-w-36rem"
            title={t`Delete`}
            label={t`Delete`}
            form={form}
            onSubmit={onSubmit}
            isActionDisabled={!watch("confirmed")}
        >
            {headerElements}

            {isMultipleFilesOrDir ? (
                <div className="pt-3">
                    <Controller
                        control={control}
                        name="confirmed"
                        {...controllerProps({ isRequired: true })}
                        render={({ field }) => (
                            <WCheckbox isSelected={field.value} onChange={field.onChange} isRequired>
                                <Trans>I'm aware that deletion is permanent</Trans>
                            </WCheckbox>
                        )}
                    />
                </div>
            ) : null}

            {isMultipleFilesOrDir ? (
                <p className="color-muted pt-3 font-size-small">
                    <Trans comment="Items: means files or folders">
                        Note: If you are deleting substantial number of items, the deletion process may result in an
                        inconsistent state, where some items are deleted while others remain. In the event of such
                        occurrences, please repeat the same action again.
                    </Trans>
                </p>
            ) : null}
        </WModalForm>
    );
}

function trimUrl(url: string, maxLength = 40) {
    return url.length > maxLength ? `${url.substring(0, maxLength)}...` : url;
}

interface LinkAge {
    /** seconds */
    value: number;
    title: string;
}

interface ShareModalProps {
    file: FileData;
    callback: (linkAge: number) => Promise<ShareLinkResult | null>;
}

function ShareModal({ file, callback }: ShareModalProps) {
    //#region Hooks
    const { t } = useLingui();
    const { s3ForcePathStyle } = useConfig();

    const linkAgeVariants: LinkAge[] = [
        { value: 60 * 60, title: t`1 hour` },
        { value: 60 * 60 * 6, title: t`6 hours` },
        { value: 60 * 60 * 24, title: t`1 day` },
        { value: 60 * 60 * 24 * 3, title: t`3 days` },
        { value: 60 * 60 * 24 * 7, title: t`7 days` },
    ];

    const [generatingLinkInProgress, setGeneratingLinkInProgress] = useState<boolean>(false);
    const [linkAge, setLinkAge] = useState<LinkAge>(linkAgeVariants[0]); // default value '1 hour'
    const [generatedLink, setGeneratedLink] = useState<ShareLinkResult | null>(null);

    useEffect(() => {
        void generateShareLink();
    }, []);

    //#endregion

    function generateShareLink(overrideAge?: number): void {
        setGeneratingLinkInProgress(true);

        const ageToUse = overrideAge ?? linkAge.value;

        setTimeout(async () => {
            setGeneratedLink(await callback(ageToUse));
            setGeneratingLinkInProgress(false);
        }, 100);
    }

    function handleLinkAgeChange(linkAge: LinkAge): void {
        setLinkAge(linkAge);
        generateShareLink(linkAge.value);
    }

    const pathStyleUrl = generatedLink?.path_style_url;
    const vhostStyleUrl = generatedLink?.vhost_style_url;

    return (
        <WModalContent title={t`Share temporarily`} modalAction={undefined}>
            <ModalRow title={t`File`}>{file.id}</ModalRow>
            <ModalRow title={t`Sharing duration`}>
                <RadioGroup
                    className={R.RadioGroup}
                    aria-label="Sharing duration"
                    value={linkAge.title}
                    orientation="horizontal"
                    onChange={(e) => handleLinkAgeChange(linkAgeVariants.find((la) => la.title === e)!)}
                >
                    {linkAgeVariants.map((option) => (
                        <Radio key={option.title} className={RA.Radio} value={option.title}>
                            {option.title}
                        </Radio>
                    ))}
                </RadioGroup>
            </ModalRow>
            <ModalRow title={t`Path style URL`}>
                {!generatingLinkInProgress ? (
                    pathStyleUrl && <ClipBoardTooltip text={pathStyleUrl}>{trimUrl(pathStyleUrl)}</ClipBoardTooltip>
                ) : (
                    <Loading />
                )}
            </ModalRow>
            {!s3ForcePathStyle && (
                <ModalRow title={t`Virtual host style URL`}>
                    {!generatingLinkInProgress ? (
                        vhostStyleUrl && (
                            <ClipBoardTooltip text={vhostStyleUrl}>{trimUrl(vhostStyleUrl)}</ClipBoardTooltip>
                        )
                    ) : (
                        <Loading />
                    )}
                </ModalRow>
            )}

            {generatedLink?.expires ? (
                <p className="color-muted pt-3 font-size-small">
                    <Trans>The link expires on:</Trans> <LongDate date={new Date(Date.now() + linkAge.value * 1000)} />
                </p>
            ) : undefined}
        </WModalContent>
    );
}

function objectIsPublic(grants: Grant[] | undefined): boolean {
    if (grants === undefined) {
        return false;
    }
    return grants.some(
        (grant) =>
            grant.Grantee?.Type === "Group" &&
            grant.Grantee?.URI === "http://acs.amazonaws.com/groups/global/AllUsers" &&
            grant.Permission === "READ",
    );
}

function PermissionsModal({
    s3,
    bucketName,
    publicUrl,
    files,
    callback,
}: {
    s3: S3Client;
    bucketName: string;
    publicUrl: string | undefined;
    files: FileData[];
    callback: (permission: "public-read" | "private") => Promise<boolean>;
}) {
    //#region Hooks
    const { t } = useLingui();
    const [isPublic, setIsPublic] = useState<boolean | undefined>(undefined);
    const [objectGrants, setObjectGrants] = useState<Grant[] | undefined>(undefined);
    const [value, setValue] = useState<"private" | "public">("private");

    useEffect(() => {
        discardPromise(getAclData());
        // eslint-disable-next-line react-hooks/exhaustive-deps -- modal props will never change
    }, []);

    //#endregion

    async function getAclData() {
        if (files.length > 1) {
            return;
        }
        try {
            const command = new GetObjectAclCommand({
                Bucket: bucketName,
                Key: files[0].id,
            });
            const data = await s3.send(command);
            const publicStatus = objectIsPublic(data.Grants);
            setObjectGrants(data.Grants);
            setIsPublic(publicStatus);
            setValue(publicStatus ? "public" : "private");
        } catch (err) {
            Toast_exception(err, "Error getting ACL data");
        }
    }

    async function handleSetPermissions(): Promise<void> {
        if (isPublic === undefined) {
            return;
        }

        await callback(isPublic ? "public-read" : "private");
        await getAclData();
        Toast_success("File(s) permissions saved successfully");
    }

    function handlePrivatePublicChange(v: string) {
        setValue(v as typeof value);
        setIsPublic(!!(v && v === "public"));
    }

    const singleFile = singleElement(files);

    const publicUrlLabel = trimUrl(publicUrl ?? "");

    return (
        <WModalContent title={t`Permissions`} label={t`Save`} modalAction={handleSetPermissions}>
            <ModalRow title={t`File`}>{singleFile ? singleFile.id : `${files.length} files`}</ModalRow>

            <ModalRow title={t`Permissions`}>
                <RadioGroup
                    className={R.RadioGroup}
                    aria-label="Permissions"
                    value={value}
                    orientation="horizontal"
                    onChange={(v) => handlePrivatePublicChange(v as typeof value)}
                >
                    <WTooltip
                        text={<Trans comment="'private' is a literal string">Set object's ACL to 'private'</Trans>}
                    >
                        <Radio
                            className={RA.Radio}
                            value="private"
                            isDisabled={singleFile !== undefined && objectGrants === undefined}
                        >
                            <Trans>Private</Trans>
                        </Radio>
                    </WTooltip>

                    <WTooltip
                        text={
                            <Trans comment="'public-read' is a literal string">Set object's ACL to 'public-read'</Trans>
                        }
                    >
                        <Radio
                            className={RA.Radio}
                            value="public"
                            isDisabled={singleFile !== undefined && objectGrants === undefined}
                        >
                            <Trans>Public</Trans>
                        </Radio>
                    </WTooltip>
                </RadioGroup>
            </ModalRow>

            {!!publicUrl && (
                <ModalRow title={t`Public URL`} ellipsis>
                    {isPublic ? (
                        <ClipBoardTooltip text={publicUrl}>{publicUrlLabel}</ClipBoardTooltip>
                    ) : (
                        <span className="color-muted p-1">{publicUrlLabel}</span>
                    )}
                </ModalRow>
            )}
        </WModalContent>
    );
}

function cleanupName(name: string) {
    name = name.replace(/\/\/+/g, "/");
    if (name.endsWith("/")) {
        name = name.slice(0, -1);
    }
    if (name.startsWith("/")) {
        name = name.substring(1);
    }
    return name;
}

interface RenameFileOrFolderInputs {
    name: string;
}

function RenameFileOrFolderModal({ file, callback }: { file: FileData; callback: (name: string) => Promise<boolean> }) {
    const { t } = useLingui();
    const [renameInProgress, setRenameInProgress] = useState<boolean>(false);

    const nameFieldTitle = file.isDir ? <Trans>New folder name</Trans> : <Trans>New file name</Trans>;

    const folderNotice = file.isDir && (
        <Trans>
            Note: If your folder contains a substantial number of files, the renaming process will take time and may
            result in an inconsistent state where some files are moved to a new location, while others remain in the old
            folder. In the event of such occurrences, please repeat the action by renaming the same folder once more.
        </Trans>
    );

    const onSubmit: SubmitHandler<RenameFileOrFolderInputs> = async (inputs) => {
        const { name } = inputs;

        setRenameInProgress(true);
        await callback(cleanupName(name));
        setRenameInProgress(false);
    };

    const form = useForm<RenameFileOrFolderInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            name: file.name,
        },
    });

    const { control } = form;

    return (
        <WModalForm
            className="max-w-36rem"
            title={t`Rename`}
            label={t`Rename`}
            form={form}
            onSubmit={onSubmit}
            footerNotice={folderNotice}
        >
            <Controller
                control={control}
                name="name"
                {...controllerProps({ isRequired: true })}
                render={(props) => (
                    <WTextField
                        autoFocus
                        {...formPropsToAriaProps(props)}
                        isRequired
                        headless
                        block
                        isDisabled={renameInProgress}
                        label={nameFieldTitle}
                    >
                        <Input className={cn(FF.FormFieldInput, TF.Input)} />
                    </WTextField>
                )}
            />
        </WModalForm>
    );
}

interface CreateFolderInputs {
    name: string;
}

function CreateFolderModal({ callback }: { callback: (name: string) => Promise<boolean> }) {
    const { t } = useLingui();
    const [createInProgress, setCreateInProgress] = useState<boolean>(false);

    const onSubmit: SubmitHandler<RenameFileOrFolderInputs> = async (inputs) => {
        const { name } = inputs;

        setCreateInProgress(true);
        await callback(cleanupName(name));
        setCreateInProgress(false);
    };

    const form = useForm<CreateFolderInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            name: "",
        },
    });

    const { control } = form;

    return (
        <WModalForm title={t`New folder`} label={t`Create`} form={form} onSubmit={onSubmit}>
            <Controller
                control={control}
                name="name"
                {...controllerProps({ isRequired: true })}
                render={(props) => (
                    <WTextField
                        autoFocus
                        {...formPropsToAriaProps(props)}
                        isRequired
                        headless
                        block
                        isDisabled={createInProgress}
                        label={t`Folder name`}
                    >
                        <Input className={cn(FF.FormFieldInput, TF.Input)} />
                    </WTextField>
                )}
            />
        </WModalForm>
    );
}
